import React, { useEffect, useState } from 'react';
import { Button, TextField, Canvas, Spinner } from 'datocms-react-ui';
import { TripFetcherProps } from '../types';

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  clearButton: {
    position: 'absolute',
    lineHeight: '20px',
    padding: '0 10px',
    background: 'transparent',
    color: 'grey',
    right: 10,
    height: 18,
    fontSize: 22,
  },
  fetchButton: {
    position: 'absolute',
    width: '22%',
    right: 0,
    bottom: -48,
    padding: 6,
    fontSize: 14,
  },
  errorMessage: {
    color: 'red',
  },
};

const TripFetcher: React.FC<TripFetcherProps> = ({ ctx }) => {
  const [tripId, setTripId] = useState<string | null>(null);
  const [tripNameWithId, setTripNameWithId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If trip id was entered and the page is reloaded, set the TextField value for that trip id
  useEffect(() => {
    const savedTripData = ctx.formValues[ctx.fieldPath] as string | null;
    if (savedTripData) {
      try {
        const parsedData = JSON.parse(savedTripData);
        const { name: tripNameWithId, id: tripIdFromData } = parsedData;
        setTripId(tripIdFromData);
        setTripNameWithId(`${tripNameWithId} (ID: ${tripIdFromData})`);
      } catch (error) {
        console.error('[plugin:bms-trip] Error parsing saved trip data:', error);
      }
    }
  // Narrow to the specific field value so this only re-runs when the saved trip changes
  // ctx intentionally omitted — it changes reference on every render and would cause re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.formValues[ctx.fieldPath]])

  const fetchTripData = async () => {
    setLoading(true);
    setError('');

    const { apiEndpoint, authorizationKey } = ctx.plugin.attributes.parameters;

    if (!tripId || !apiEndpoint || !authorizationKey) {
      setError('Please provide a valid Trip ID, API endpoint, and authorization key.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiEndpoint}/trips/${tripId}/marketing`, {
        headers: {
          Authorization: `${authorizationKey}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch trip data:');
      }

      const result = await response.json();
      const { name: tripNameWithId, id: tripIdFromData } = result;

      setTripNameWithId(`${tripNameWithId} (ID: ${tripIdFromData})`);
      await ctx.setFieldValue(ctx.fieldPath, JSON.stringify(result));
    } catch (error: any) {
      setError(error.message || 'Error fetching trip data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setTripId(value || null);
    setTripNameWithId(null);
  };

  const clearInputField = async () => {
    setTripId(null);
    setTripNameWithId(null);
    setError('');
    await ctx.setFieldValue(ctx.fieldPath, null);
  };

  return (
    <Canvas ctx={ctx}>
      <div style={styles.container}>
        <TextField
          id="bms-trip-json-input"
          name="bmsTripJsonInput"
          label=""
          placeholder="Enter Trip ID"
          value={tripNameWithId ?? tripId ?? ''}
          onChange={handleInputChange}
        />

        {tripId && (
          <Button onClick={clearInputField} buttonSize="m" style={styles.clearButton as React.CSSProperties}>
            ×
          </Button>
        )}

        <Button
          onClick={fetchTripData}
          buttonType="primary"
          style={styles.fetchButton as React.CSSProperties}
          disabled={loading || !tripId}
        >
          {loading ? <Spinner size={16} /> : tripNameWithId ? 'Refresh Data' : 'Fetch Trip Data'}
        </Button>
      </div>

      {error && <p style={styles.errorMessage as React.CSSProperties}>{error}</p>}
    </Canvas>
  );
};

export default TripFetcher;
